import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';

export default function RefundPolicy() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const sections = [
    { id: 'general-policy', title: '1. General Policy' },
    { id: 'eligibility', title: '2. Eligibility for Refunds' },
    { id: 'non-refundable', title: '3. Non-Refundable Situations' },
    { id: 'event-cancellations', title: '4. Event Cancellations' },
    { id: 'event-changes', title: '5. Event Changes' },
    { id: 'how-to-request', title: '6. How to Request a Refund' },
    { id: 'processing-time', title: '7. Processing Time' },
    { id: 'organizer-responsibility', title: '8. Organizer Responsibility' },
    { id: 'fraud-abuse', title: '9. Fraud & Abuse' },
    { id: 'contact-us', title: '10. Contact Us' },
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="refund-policy-title">Refund Policy</h1>
          <p className="text-muted-foreground">Last Updated: 1st April 2026</p>
        </div>

        {/* Intro */}
        <div className="mb-10">
          <p className="text-muted-foreground leading-relaxed">
            At NextGig, we aim to provide a fair and transparent refund experience for both event attendees and organizers. Please review our policy below before purchasing tickets.
          </p>
        </div>

        {/* Table of Contents */}
        <nav className="mb-12 p-6 bg-muted/30 rounded-lg border border-border" data-testid="refund-policy-toc">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
            Table of Contents
          </h2>
          <ul className="grid sm:grid-cols-2 gap-2">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <div className="space-y-10" data-testid="refund-policy-content">

          {/* Section 1 */}
          <section id="general-policy">
            <h2 className="text-xl md:text-2xl font-bold mb-4">1. General Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Refunds for tickets purchased through NextGig are subject to the terms set by the event organizer. Each event may have its own specific refund policy, which will be displayed on the event page.
            </p>
          </section>

          {/* Section 2 */}
          <section id="eligibility">
            <h2 className="text-xl md:text-2xl font-bold mb-4">2. Eligibility for Refunds</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              You may be eligible for a refund if:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>The event is cancelled</li>
              <li>The event is significantly changed (e.g., major change in date, time, or venue)</li>
              <li>The organizer has enabled refunds for the event</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Refund requests must be made within the timeframe specified on the event listing.
            </p>
          </section>

          {/* Section 3 */}
          <section id="non-refundable">
            <h2 className="text-xl md:text-2xl font-bold mb-4">3. Non-Refundable Situations</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Refunds may not be available if:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>You can no longer attend the event for personal reasons</li>
              <li>The refund request is made after the allowed period</li>
              <li>The event is marked as non-refundable by the organizer</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section id="event-cancellations">
            <h2 className="text-xl md:text-2xl font-bold mb-4">4. Event Cancellations</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              If an event is cancelled:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>You will be notified via email</li>
              <li>You may be eligible for a full refund of the ticket price</li>
              <li>Refund processing times may vary depending on the payment provider</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section id="event-changes">
            <h2 className="text-xl md:text-2xl font-bold mb-4">5. Event Changes</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              If there are significant changes to an event:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>You will be informed of the changes</li>
              <li>You may request a refund if the changes materially affect your ability to attend</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section id="how-to-request">
            <h2 className="text-xl md:text-2xl font-bold mb-4">6. How to Request a Refund</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              To request a refund:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Log into your NextGig account and navigate to your tickets</li>
              <li>Follow the refund request instructions, or</li>
              <li>Contact support on the platform</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section id="processing-time">
            <h2 className="text-xl md:text-2xl font-bold mb-4">7. Processing Time</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Approved refunds are typically processed within 3 business days</li>
              <li>The time it takes for funds to appear in your account depends on your payment provider</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section id="organizer-responsibility">
            <h2 className="text-xl md:text-2xl font-bold mb-4">8. Organizer Responsibility</h2>
            <p className="text-muted-foreground leading-relaxed">
              Event organizers are responsible for setting and honoring their refund policies. NextGig facilitates the process but does not control individual event policies.
            </p>
          </section>

          {/* Section 9 */}
          <section id="fraud-abuse">
            <h2 className="text-xl md:text-2xl font-bold mb-4">9. Fraud & Abuse</h2>
            <p className="text-muted-foreground leading-relaxed">
              NextGig reserves the right to deny refund requests in cases of suspected fraud, abuse, or violation of our Terms of Service.
            </p>
          </section>

          {/* Section 10 */}
          <section id="contact-us">
            <h2 className="text-xl md:text-2xl font-bold mb-4">10. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have any questions about this Refund Policy, please contact us at:
            </p>
            <div className="bg-muted/50 rounded-lg p-6">
              <a
                href="mailto:Nextgigsuite@gmail.com"
                className="text-primary hover:underline font-medium"
              >
                Nextgigsuite@gmail.com
              </a>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
